import { useState, useRef, useEffect, useCallback } from 'react';
import { ContaBancaria } from '../../mocks/contasBancarias';
import { useContasBancarias } from '../../contexts/ContasBancariasContext';
import { calcularPontosFinanceiro } from '../../utils/pontosFinanceiro';
import { useBottomSheet } from '../../hooks/useBottomSheet';
import { getAuthSession } from '@/hooks/useAuth';
import { enviarEmailConfirmacaoTransacao } from '@/services/emailService';

interface CreditoDisponivel {
  id: string;
  descricao: string;
  valor: number;
  campanha: string;
  dataCredito: string;
}

interface PixModalProps {
  isOpen: boolean;
  onClose: () => void;
  saldoDisponivel: number;
  onTransferenciaRealizada?: (
    valor: number,
    chave: string,
    descricao: string,
    tipoTransacao: 'pix'
  ) => void;
}

// Créditos disponíveis que compõem o saldo
const creditosDisponiveis: CreditoDisponivel[] = [
  {
    id: '1',
    descricao: 'Bônus Meta de Vendas',
    valor: 2000.0,
    campanha: 'Verão 2024',
    dataCredito: '24/12/2025',
  },
  {
    id: '2',
    descricao: 'Comissão Vendas Janeiro',
    valor: 1500.0,
    campanha: 'Programa Anual',
    dataCredito: '15/01/2025',
  },
  {
    id: '3',
    descricao: 'Premiação Trimestral',
    valor: 850.0,
    campanha: 'Q4 2024',
    dataCredito: '10/01/2025',
  },
  {
    id: '4',
    descricao: 'Bônus Indicação',
    valor: 500.0,
    campanha: 'Indique e Ganhe',
    dataCredito: '05/01/2025',
  },
];

// Funções auxiliares para gerar IDs únicos
function gerarTransacaoId(): string {
  return (
    'TXN' +
    Date.now().toString(36).toUpperCase() +
    Math.random().toString(36).substring(2, 6).toUpperCase()
  );
}

function gerarCodigoValidacao(): string {
  return Math.random().toString(36).substring(2, 10).toUpperCase();
}

/** Mascara CPF (11 dígitos) ou CNPJ (14 dígitos) para exibição segura */
function mascararDocumento(doc: string): string {
  const numeros = doc.replace(/\D/g, '');
  if (numeros.length === 11) {
    return `***.${numeros.slice(3, 6)}.${numeros.slice(6, 9)}-**`;
  }
  if (numeros.length === 14) {
    return `**.${numeros.slice(2, 5)}.${numeros.slice(5, 8)}/${numeros.slice(8, 12)}-**`;
  }
  return doc;
}

// Função auxiliar para desenhar QR Code simulado no canvas
function desenharQRCode(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  _data: string
): void {
  const half = size / 2;
  const cellSize = size / 21;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(cx - half - 8, cy - half - 8, size + 16, size + 16);
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 1;
  ctx.strokeRect(cx - half - 8, cy - half - 8, size + 16, size + 16);

  // Gera padrão pseudo‑aleatório determinístico
  for (let row = 0; row < 21; row++) {
    for (let col = 0; col < 21; col++) {
      const isFinderPattern =
        (row < 7 && col < 7) ||
        (row < 7 && col > 13) ||
        (row > 13 && col < 7);

      let filled = false;

      if (isFinderPattern) {
        const inOuter =
          (row === 0 || row === 6 || col === 0 || col === 6) ||
          (row > 13 && (row === 14 || row === 20 || col === 0 || col === 6)) ||
          (col > 13 && (row === 0 || row === 6 || col === 14 || col === 20));
        const inInner =
          (row >= 2 && row <= 4 && col >= 2 && col <= 4) ||
          (row >= 2 && row <= 4 && col >= 16 && col <= 18) ||
          (row >= 16 && row <= 18 && col >= 2 && col <= 4);
        filled = inOuter || inInner;
      } else {
        filled = (row * 3 + col * 7 + row * col) % 3 === 0;
      }

      if (filled) {
        ctx.fillStyle = '#1e293b';
        ctx.fillRect(
          cx - half + col * cellSize,
          cy - half + row * cellSize,
          cellSize - 0.5,
          cellSize - 0.5
        );
      }
    }
  }

  ctx.fillStyle = '#10b981';
  ctx.font = `bold ${cellSize * 1.8}px Arial`;
  ctx.textAlign = 'center';
  ctx.fillText('GP', cx, cy + cellSize * 0.7);
}

export function PixModal({
  isOpen,
  onClose,
  saldoDisponivel,
  onTransferenciaRealizada,
}: PixModalProps) {
  // Contas vindas do contexto compartilhado (mesma fonte que o perfil)
  const { contas, definirPrincipal, removerConta } = useContasBancarias();
  const [definindoPrincipalId, setDefinindoPrincipalId] = useState<number | null>(null);
  const [toastPrincipal, setToastPrincipal] = useState<string | null>(null);
  const [confirmandoExclusaoId, setConfirmandoExclusaoId] = useState<number | null>(null);
  const [excluindoId, setExcluindoId] = useState<number | null>(null);

  // Encontrar a conta principal para pré‑selecionar
  const contaPrincipal = contas.find((c) => c.principal);

  const [tipoChave, setTipoChave] = useState<'cpf' | 'email' | 'celular' | 'aleatoria'>(
    contaPrincipal?.tipoPix ?? 'cpf'
  );
  const [chave, setChave] = useState(contaPrincipal?.chavePix ?? '');
  const [modoChave, setModoChave] = useState<'contas' | 'digitar'>('contas');
  const [creditoSelecionado, setCreditoSelecionado] = useState<string>('');
  const [descricao, setDescricao] = useState('');
  const [etapa, setEtapa] = useState<'formulario' | 'token' | 'sucesso'>('formulario');
  const [processando, setProcessando] = useState(false);
  const [tokenDigits, setTokenDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [tokenEnviado, setTokenEnviado] = useState(false);
  const [tokenErro, setTokenErro] = useState('');
  const [reenvioTimer, setReenvioTimer] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [salvandoPdf, setSalvandoPdf] = useState(false);
  const [salvandoImagem, setSalvandoImagem] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [gerandoImagem, setGerandoImagem] = useState(false);
  const [compartilhando, setCompartilhando] = useState(false);

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailDestinatario, setEmailDestinatario] = useState('');
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [emailEnviado, setEmailEnviado] = useState(false);
  const [emailErro, setEmailErro] = useState('');

  // Refs para dados da transação (gerados no momento da confirmação)
  const transacaoId = useRef<string>('');
  const codigoValidacao = useRef<string>('');
  const dataHoraTransacao = useRef<string>('');

  // Valores derivados do crédito selecionado
  const creditoAtual = creditosDisponiveis.find((c) => c.id === creditoSelecionado) ?? null;
  const valorNumerico = creditoAtual?.valor ?? 0;
  const pontosNecessarios = calcularPontosFinanceiro(valorNumerico);

  // Label legível do tipo de chave PIX
  const tipoChaveLabel = (() => {
    switch (tipoChave) {
      case 'cpf':
        return 'CPF/CNPJ';
      case 'email':
        return 'E‑mail';
      case 'celular':
        return 'Celular';
      case 'aleatoria':
        return 'Chave Aleatória';
      default:
        return tipoChave;
    }
  })();

  // Função para definir conta como principal (atualiza o contexto global)
  const handleDefinirPrincipal = useCallback(
    async (conta: ContaBancaria) => {
      if (conta.principal || definindoPrincipalId !== null) return;
      setDefinindoPrincipalId(conta.id);
      await new Promise((resolve) => setTimeout(resolve, 1200));
      definirPrincipal(conta.id);
      setTipoChave(conta.tipoPix);
      setChave(conta.chavePix);
      setDefinindoPrincipalId(null);
      setToastPrincipal(conta.banco);
      setTimeout(() => setToastPrincipal(null), 3500);
    },
    [definindoPrincipalId, definirPrincipal]
  );

  // Função para excluir conta
  const handleExcluirConta = useCallback(
    async (conta: ContaBancaria) => {
      if (excluindoId !== null) return;
      setExcluindoId(conta.id);
      await new Promise((resolve) => setTimeout(resolve, 800));

      // Se a conta excluída estava selecionada, limpar seleção
      if (chave === conta.chavePix && tipoChave === conta.tipoPix) {
        const principal = contas.find((c) => c.principal && c.id !== conta.id);
        setTipoChave(principal?.tipoPix ?? 'cpf');
        setChave(principal?.chavePix ?? '');
      }

      removerConta(conta.id);
      setExcluindoId(null);
      setConfirmandoExclusaoId(null);
    },
    [excluindoId, chave, tipoChave, contas, removerConta]
  );

  // Função para gerar texto do comprovante
  const getComprovanteTexto = useCallback(() => {
    return `
═══════════════════════════════════════
       COMPROVANTE DE TRANSFERÊNCIA PIX
════════════════════════════════───────

✓ Transferência Concluída

ID da Transação: ${transacaoId.current}

───────────────────────────────────────
DADOS DA TRANSFERÊNCIA
───────────────────────────────────────
Valor: R$ ${valorNumerico.toFixed(2)}
Campanha: ${creditoAtual?.campanha || 'N/A'}
Data: ${dataHoraTransacao.current || new Date().toLocaleString('pt-BR')}

───────────────────────────────────────
DESTINATÁRIO
───────────────────────────────────────
Tipo de Chave: ${tipoChaveLabel}
Chave PIX: ${chave}
${descricao ? `Descrição: ${descricao}` : ''}

───────────────────────────────────────
Este comprovante é válido como recibo
de transferência PIX.
───────────────────────────────────────
© 2024 Getpoints Tecnologia Ltda
`.trim();
  }, [valorNumerico, creditoAtual, tipoChaveLabel, chave, descricao]);

  // Função para gerar PDF (na verdade salva como TXT)
  const gerarComprovantePDF = useCallback(async () => {
    setSalvandoPdf(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      const elemento = document.createElement('a');
      const texto = getComprovanteTexto();
      const blob = new Blob([texto], { type: 'text/plain' });
      elemento.href = URL.createObjectURL(blob);
      elemento.download = `comprovante-pix-${transacaoId.current}.txt`;
      document.body.appendChild(elemento);
      elemento.click();
      document.body.removeChild(elemento);
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
    } finally {
      setSalvandoPdf(false);
    }
  }, [getComprovanteTexto]);

  // Função para gerar imagem do comprovante
  const gerarImagemComprovante = useCallback(async (): Promise<Blob> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = 800;
      canvas.height = 1300;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        console.error('Canvas não suportado');
        resolve(new Blob());
        return;
      }

      // Fundo branco
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Gradiente superior
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
      gradient.addColorStop(0, '#059669');
      gradient.addColorStop(1, '#10b981');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, 200);

      // Logo
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      logoImg.src =
        'https://static.readdy.ai/image/22e92c18f0b031d861a5e53bf8e362fb/283dcc9a7c4c598174df7f942b83da1f.png';

      const renderComprovante = (hasLogo: boolean) => {
        if (hasLogo) {
          const logoWidth = 220;
          const logoHeight = 90;
          const logoX = (canvas.width - logoWidth) / 2;
          const logoY = 30;

          ctx.fillStyle = '#ffffff';
          ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
          ctx.shadowBlur = 15;
          ctx.shadowOffsetY = 5;
          ctx.beginPath();
          // @ts-expect-error – roundRect está disponível em navegadores modernos
          ctx.roundRect(logoX - 20, logoY - 15, logoWidth + 40, logoHeight + 30, 16);
          ctx.fill();
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          ctx.shadowOffsetY = 0;

          ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
        } else {
          ctx.fillStyle = '#ffffff';
          ctx.font = 'bold 48px Arial';
          ctx.textAlign = 'center';
          ctx.fillText('GETPOINTS', canvas.width / 2, 80);
        }

        // Cabeçalho
        ctx.fillStyle = '#ffffff';
        ctx.font = '18px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Programa de Incentivos', canvas.width / 2, 165);

        ctx.beginPath();
        ctx.arc(canvas.width / 2, 185, 22, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();

        ctx.strokeStyle = '#10b981';
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(canvas.width / 2 - 9, 185);
        ctx.lineTo(canvas.width / 2 - 3, 191);
        ctx.lineTo(canvas.width / 2 + 9, 179);
        ctx.stroke();

        ctx.fillStyle = '#1e293b';
        ctx.font = 'bold 34px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Comprovante de Transferência PIX', canvas.width / 2, 260);

        ctx.fillStyle = '#10b981';
        ctx.font = 'bold 22px Arial';
        ctx.fillText('✓ Transferência Concluída', canvas.width / 2, 295);

        ctx.strokeStyle = '#e2e8f0';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(60, 325);
        ctx.lineTo(canvas.width - 60, 325);
        ctx.stroke();

        // Área de informações
        ctx.fillStyle = '#f0fdf4';
        ctx.fillRect(60, 345, canvas.width - 120, 70);
        ctx.strokeStyle = '#10b981';
        ctx.lineWidth = 2;
        ctx.strokeRect(60, 345, canvas.width - 120, 70);

        ctx.fillStyle = '#64748b';
        ctx.font = '16px Arial';
        ctx.textAlign = 'left';
        ctx.fillText('ID da Transação', 80, 375);

        ctx.fillStyle = '#1e293b';
        ctx.font = 'bold 20px monospace';
        ctx.fillText(transacaoId.current, 80, 400);

        let yPos = 455;

        const desenharSecao = (
          titulo: string,
          dados: Array<{ label: string; valor: string }>
        ) => {
          ctx.fillStyle = '#1e293b';
          ctx.font = 'bold 20px Arial';
          ctx.textAlign = 'left';
          ctx.fillText(titulo, 70, yPos);
          yPos += 35;
          dados.forEach(({ label, valor }) => {
            ctx.fillStyle = '#64748b';
            ctx.font = '16px Arial';
            ctx.fillText(label, 80, yPos);
            ctx.fillStyle = '#1e293b';
            ctx.font = '18px Arial';
            ctx.textAlign = 'right';
            ctx.fillText(valor, canvas.width - 80, yPos);
            ctx.textAlign = 'left';
            yPos += 30;
          });
          yPos += 20;
        };

        desenharSecao('💰 Dados da Transferência', [
          { label: 'Valor', valor: `R$ ${valorNumerico.toFixed(2)}` },
          { label: 'Campanha', valor: creditoAtual?.campanha || 'N/A' },
          {
            label: 'Data',
            valor: dataHoraTransacao.current || new Date().toLocaleString('pt-BR'),
          },
        ]);

        desenharSecao('👤 Destinatário', [
          { label: 'Tipo de Chave', valor: tipoChaveLabel },
          { label: 'Chave PIX', valor: chave },
        ]);

        // Código de validação
        ctx.fillStyle = '#f0fdf4';
        ctx.fillRect(60, yPos, canvas.width - 120, 80);
        ctx.strokeStyle = '#10b981';
        ctx.lineWidth = 1.5;
        ctx.strokeRect(60, yPos, canvas.width - 120, 80);

        ctx.fillStyle = '#64748b';
        ctx.font = '14px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Código de Validação', canvas.width / 2, yPos + 25);

        ctx.fillStyle = '#059669';
        ctx.font = 'bold 18px monospace';
        ctx.fillText(codigoValidacao.current, canvas.width / 2, yPos + 55);

        yPos += 105;

        // QR Code
        const qrData = `https://getpoints.com.br/validar?tx=${transacaoId.current}&code=${codigoValidacao.current}`;
        const qrSize = 160;
        desenharQRCode(ctx, canvas.width / 2, yPos, qrSize, qrData);

        yPos += qrSize + 50;

        // Marca d'água
        ctx.save();
        ctx.globalAlpha = 0.05;
        ctx.fillStyle = '#10b981';
        ctx.font = 'bold 80px Arial';
        ctx.translate(canvas.width / 2, yPos);
        ctx.rotate(-Math.PI / 12);
        ctx.textAlign = 'center';
        ctx.fillText('GETPOINTS', 0, 0);
        ctx.restore();

        yPos += 60;

        ctx.fillStyle = '#f8fafc';
        ctx.fillRect(0, yPos, canvas.width, canvas.height - yPos);

        ctx.fillStyle = '#64748b';
        ctx.font = '12px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('Este comprovante é válido como recibo de transferência PIX', canvas.width / 2, yPos + 25);
        ctx.fillText('Guarde este comprovante para seus registros', canvas.width / 2, yPos + 45);
        ctx.fillText('Em caso de dúvidas, entre em contato com o suporte', canvas.width / 2, yPos + 65);

        ctx.fillStyle = '#94a3b8';
        ctx.font = '11px Arial';
        ctx.fillText('© 2024 Getpoints Tecnologia Ltda - Todos os direitos reservados', canvas.width / 2, yPos + 90);

        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else resolve(new Blob());
        }, 'image/png');
      };

      logoImg.onload = () => renderComprovante(true);
      logoImg.onerror = () => renderComprovante(false);
    });
  }, [valorNumerico, creditoAtual, tipoChaveLabel, chave, codigoValidacao, transacaoId, dataHoraTransacao]);

  // Função para salvar imagem
  const handleSalvarImagem = useCallback(async () => {
    setSalvandoImagem(true);
    try {
      const blob = await gerarImagemComprovante();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `comprovante-pix-${transacaoId.current}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao salvar imagem:', error);
    } finally {
      setSalvandoImagem(false);
    }
  }, [gerarImagemComprovante]);

  // Função principal de compartilhamento nativo com imagem
  const handleCompartilhar = useCallback(async () => {
    setCompartilhando(true);
    try {
      const blob = await gerarImagemComprovante();
      const file = new File([blob], `comprovante-pix-${transacaoId.current}.png`, {
        type: 'image/png',
      });

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: 'Comprovante PIX - GetPoints',
          text: `Comprovante de transferência PIX\nValor: R$ ${valorNumerico.toFixed(
            2
          )}\nChave: ${chave}\nID: ${transacaoId.current}`,
          files: [file],
        });
      } else if (navigator.share) {
        await navigator.share({
          title: 'Comprovante PIX - GetPoints',
          text: `Comprovante de transferência PIX\nValor: R$ ${valorNumerico.toFixed(
            2
          )}\nChave PIX: ${chave}\nID: ${transacaoId.current}\nData: ${dataHoraTransacao.current}`,
        });
      } else {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `comprovante-pix-${transacaoId.current}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error: any) {
      if (error?.name !== 'AbortError') {
        console.error('Erro ao compartilhar:', error);
      }
    } finally {
      setCompartilhando(false);
    }
  }, [gerarImagemComprovante, valorNumerico, chave, dataHoraTransacao]);

  // Função para enviar comprovante por e‑mail
  const handleEnviarEmail = useCallback(async () => {
    if (!emailDestinatario || !emailDestinatario.includes('@')) {
      setEmailErro('Por favor, insira um e‑mail válido.');
      return;
    }

    setEnviandoEmail(true);
    setEmailErro('');

    try {
      await new Promise((resolve) => setTimeout(resolve, 2500));
      setEmailEnviado(true);
      setTimeout(() => {
        setEmailEnviado(false);
        setEmailModalOpen(false);
        setEmailDestinatario('');
      }, 4000);
    } catch {
      setEmailErro('Falha ao enviar o e‑mail. Tente novamente.');
    } finally {
      setEnviandoEmail(false);
    }
  }, [emailDestinatario]);

  // Timer para reenvio do token
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    if (reenvioTimer > 0) {
      interval = setInterval(() => {
        setReenvioTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [reenvioTimer]);

  // Focar no primeiro input ao entrar na etapa token
  useEffect(() => {
    if (etapa === 'token' && inputRefs.current[0]) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [etapa]);

  const handleFechar = useCallback(() => {
    setEtapa('formulario');
    const principal = contas.find((c) => c.principal);
    setTipoChave(principal?.tipoPix ?? 'cpf');
    setChave(principal?.chavePix ?? '');
    setCreditoSelecionado('');
    setDescricao('');
    setTokenDigits(['', '', '', '', '', '']);
    setTokenEnviado(false);
    setTokenErro('');
    setReenvioTimer(0);
    setGerandoImagem(false);
    setEmailModalOpen(false);
    setEmailDestinatario('');
    setEmailEnviado(false);
    setEmailErro('');
    onClose();
  }, [contas, onClose]);

  const { mounted, sheetClass, backdropClass, handleClose: animatedClose } = useBottomSheet({ isOpen, onClose: handleFechar });

  if (!mounted) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTokenEnviado(true);
    setReenvioTimer(60);
    setTokenDigits(['', '', '', '', '', '']);
    setTokenErro('');
    setEtapa('token');
  };

  const handleTokenChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;
    const newDigits = [...tokenDigits];
    newDigits[index] = value;
    setTokenDigits(newDigits);
    setTokenErro('');
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleTokenKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !tokenDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleTokenPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length > 0) {
      const newDigits = [...tokenDigits];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newDigits[i] = pastedData[i];
      }
      setTokenDigits(newDigits);
      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleReenviarToken = () => {
    if (reenvioTimer > 0) return;
    setReenvioTimer(60);
    setTokenDigits(['', '', '', '', '', '']);
    setTokenErro('');
    inputRefs.current[0]?.focus();
  };

  const tokenCompleto = tokenDigits.every((d) => d !== '');

  const handleConfirmarToken = async () => {
    if (!tokenCompleto) return;
    setProcessando(true);
    await new Promise((resolve) => setTimeout(resolve, 2000));
    const tokenValue = tokenDigits.join('');
    if (tokenValue === '000000') {
      setTokenErro('Token inválido. Verifique e tente novamente.');
      setProcessando(false);
      return;
    }
    if (onTransferenciaRealizada && creditoAtual) {
      onTransferenciaRealizada(
        valorNumerico,
        chave,
        descricao || `Resgate PIX - ${creditoAtual.descricao}`,
        'pix'
      );
    }
    dataHoraTransacao.current = new Date().toLocaleString('pt-BR');
    transacaoId.current = gerarTransacaoId();
    codigoValidacao.current = gerarCodigoValidacao();

    // Enviar email de confirmação via API do backend (fire-and-forget)
    const session = getAuthSession();
    if (session.email && session.nome) {
      const agora = new Date();

      // Montar dados do destinatário a partir da conta selecionada
      const contaDestino = contas.find(c => c.chavePix === chave && c.tipoPix === tipoChave);
      const destinatario = contaDestino ? {
        nome: contaDestino.titular,
        documento: mascararDocumento(contaDestino.cpf),
        chavePix: contaDestino.chavePix,
        tipoChavePix: tipoChaveLabel,
        dadosBancarios: {
          banco: contaDestino.banco,
          agencia: contaDestino.agencia,
          conta: contaDestino.conta,
          tipoConta: contaDestino.tipo,
        },
      } : undefined;

      enviarEmailConfirmacaoTransacao({
        nomeParticipante: session.nome,
        emailParticipante: session.email,
        tipo: 'resgate',
        valor: valorNumerico,
        data: agora.toLocaleDateString('pt-BR'),
        hora: agora.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        idTransacao: transacaoId.current,
        descricao: descricao || `Resgate PIX - ${creditoAtual?.descricao || ''}`,
        minutosExpiracao: 90,
        destinatario,
      });
    }

    setProcessando(false);
    setEtapa('sucesso');
  };

  const valorValido = valorNumerico > 0 && valorNumerico <= saldoDisponivel;

  return (
    <div
      className={`fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center sm:p-4 z-50 ${backdropClass}`}
      onClick={animatedClose}
    >
      <div
        className={`bg-white sm:rounded-2xl rounded-t-2xl max-w-md w-full flex flex-col ${sheetClass}`}
        style={{ maxHeight: 'calc(100dvh - 64px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-5 py-4 relative flex-shrink-0 rounded-t-2xl">
          <button
            onClick={animatedClose}
            className="absolute top-3 right-3 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-exchange-dollar-line text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold">Transferência PIX</h3>
              <p className="text-xs opacity-90">
                {etapa === 'formulario' && 'Envie dinheiro instantaneamente'}
                {etapa === 'token' && 'Confirme com o token enviado'}
                {etapa === 'sucesso' && 'Transferência concluída'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <div
              className={`h-1 flex-1 rounded-full transition-all ${
                etapa === 'formulario' ? 'bg-white' : 'bg-white/40'
              }`}
            ></div>
            <div
              className={`h-1 flex-1 rounded-full transition-all ${
                etapa === 'token' ? 'bg-white' : 'bg-white/40'
              }`}
            ></div>
            <div
              className={`h-1 flex-1 rounded-full transition-all ${
                etapa === 'sucesso' ? 'bg-white' : 'bg-white/40'
              }`}
            ></div>
          </div>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1 pb-20">
          {/* Etapa: Formulário */}
          {etapa === 'formulario' && (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2.5">
                <div className="text-xs text-green-700 mb-0.5">Pontos Disponíveis</div>
                <div className="text-xl font-bold text-green-600">
                  {saldoDisponivel.toLocaleString('pt-BR')} pts
                </div>
              </div>

              <div>
                <label
                  htmlFor="credito-select"
                  className="block text-xs font-medium text-slate-700 mb-1.5"
                >
                  Selecione o Crédito para Resgate
                </label>
                <select
                  id="credito-select"
                  data-tour="pix-credito-select"
                  value={creditoSelecionado}
                  onChange={(e) => setCreditoSelecionado(e.target.value)}
                  required
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white cursor-pointer text-sm"
                >
                  <option value="">Selecione um crédito...</option>
                  {creditosDisponiveis.map((credito) => (
                    <option key={credito.id} value={credito.id}>
                      {credito.descricao} - R${' '}
                      {credito.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} (
                      {credito.campanha})
                    </option>
                  ))}
                </select>
              </div>

              {creditoAtual && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-blue-700">Crédito</span>
                    <span className="text-xs font-semibold text-blue-900">{creditoAtual.descricao}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-blue-700">Campanha</span>
                    <span className="text-xs font-medium text-blue-800">{creditoAtual.campanha}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-blue-700">Data do Crédito</span>
                    <span className="text-xs font-medium text-blue-800">{creditoAtual.dataCredito}</span>
                  </div>
                  {/* Resumo de pontos para o crédito selecionado */}
                  <div className="border-t border-blue-200 pt-2 mt-1">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-blue-700">Receber via PIX</span>
                      <span className="text-xs font-semibold text-blue-900">
                        R$ {creditoAtual.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs font-bold text-green-700">Pontos necessários</span>
                      <span className="text-sm font-bold text-green-700">
                        {pontosNecessarios.toLocaleString('pt-BR')} pts
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
                  <label className="block text-xs font-semibold text-slate-700 mb-2">
                    Escolha uma opção:
                  </label>
                  <div className="flex bg-slate-200 rounded-full p-1 gap-1">
                    <button
                      type="button"
                      onClick={() => setModoChave('contas')}
                      className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-full text-[11px] font-semibold transition-all cursor-pointer ${
                        modoChave === 'contas'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <i className="ri-bank-card-line text-xs flex-shrink-0"></i>
                      <span>Favoritos</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setModoChave('digitar')}
                      className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-full text-[11px] font-semibold transition-all cursor-pointer ${
                        modoChave === 'digitar'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <i className="ri-keyboard-line text-xs flex-shrink-0"></i>
                      <span>Digitar Chave</span>
                    </button>
                  </div>
                </div>

                <div className="p-3">
                  {modoChave === 'contas' && (
                    <div className="space-y-1.5">
                      {contas.length === 0 ? (
                        <div className="text-center py-6">
                          <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-2">
                            <i className="ri-bank-card-line text-slate-400 text-xl"></i>
                          </div>
                          <p className="text-xs text-slate-500 font-medium">
                            Nenhuma conta cadastrada
                          </p>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            Cadastre uma conta no seu perfil
                          </p>
                        </div>
                      ) : (
                        contas.map((conta) => {
                          const isSelected = chave === conta.chavePix && tipoChave === conta.tipoPix;
                          const isDefinindo = definindoPrincipalId === conta.id;
                          const isConfirmandoExclusao = confirmandoExclusaoId === conta.id;
                          const isExcluindo = excluindoId === conta.id;
                          return (
                            <div
                              key={conta.id}
                              className={`w-full rounded-xl border-2 transition-all overflow-hidden ${
                                isSelected
                                  ? 'border-green-500 bg-green-50'
                                  : isConfirmandoExclusao
                                  ? 'border-red-300 bg-red-50'
                                  : 'border-slate-200 bg-white hover:border-slate-300'
                              }`}
                            >
                              {isConfirmandoExclusao ? (
                                // Mini confirmação de exclusão
                                <div className="flex items-center gap-2 p-3">
                                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-semibold text-red-700 truncate">
                                      Excluir {conta.banco}?
                                    </p>
                                    <p className="text-[10px] text-red-500">
                                      Esta ação não pode ser desfeita
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    <button
                                      type="button"
                                      onClick={() => setConfirmandoExclusaoId(null)}
                                      disabled={isExcluindo}
                                      className="px-2 py-1 text-[10px] font-semibold bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                                    >
                                      Cancelar
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleExcluirConta(conta)}
                                      disabled={isExcluindo}
                                      className="px-2 py-1 text-[10px] font-semibold bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-1"
                                    >
                                      {isExcluindo ? (
                                        <i className="ri-loader-4-line animate-spin text-[10px]"></i>
                                      ) : (
                                        <i className="ri-delete-bin-line text-[10px]"></i>
                                      )}
                                      {isExcluindo ? 'Excluindo...' : 'Excluir'}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setTipoChave(conta.tipoPix);
                                    setChave(conta.chavePix);
                                  }}
                                  className="w-full flex items-center gap-3 p-3 cursor-pointer group"
                                >
                                  <div
                                    className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                      conta.principal ? 'bg-amber-100' : 'bg-slate-100'
                                    }`}
                                  >
                                    <i
                                      className={`ri-bank-card-line text-base ${
                                        conta.principal ? 'text-amber-600' : 'text-slate-500'
                                      }`}
                                    ></i>
                                  </div>
                                  <div className="flex-1 min-w-0 text-left">
                                    <div className="flex items-center gap-1.5 mb-0.5">
                                      <span className="text-xs font-bold text-slate-900 truncate">
                                        {conta.banco}
                                      </span>
                                      {conta.principal ? (
                                        <span className="flex items-center gap-1 text-[9px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap flex-shrink-0">
                                          <i className="ri-star-fill text-[8px]"></i>
                                          Principal
                                        </span>
                                      ) : (
                                        <button
                                          type="button"
                                          title="Definir como conta principal"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDefinirPrincipal(conta);
                                          }}
                                          disabled={definindoPrincipalId !== null}
                                          className={`opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap flex-shrink-0 cursor-pointer ${
                                            definindoPrincipalId === conta.id
                                              ? 'bg-amber-100 text-amber-500 opacity-100'
                                              : 'bg-slate-100 text-slate-400 hover:bg-amber-50 hover:text-amber-600'
                                          }`}
                                        >
                                          {definindoPrincipalId === conta.id ? (
                                            <i className="ri-loader-4-line animate-spin text-[9px]"></i>
                                          ) : (
                                            <i className="ri-star-line text-[9px]"></i>
                                          )}
                                          {definindoPrincipalId === conta.id
                                            ? 'Definindo...'
                                            : 'Definir principal'}
                                        </button>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <i className="ri-key-2-line text-green-500 text-[10px]"></i>
                                      <span className="text-[10px] text-green-700 font-medium truncate">
                                        {conta.tipoPix.toUpperCase()}: {conta.chavePix}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    {/* Botão excluir — aparece no hover */}
                                    <button
                                      type="button"
                                      title="Excluir conta"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setConfirmandoExclusaoId(conta.id);
                                      }}
                                      className="opacity-0 group-hover:opacity-100 transition-opacity w-6 h-6 flex items-center justify-center rounded-full bg-slate-100 text-slate-400 hover:bg-red-100 hover:text-red-500 cursor-pointer"
                                    >
                                      <i className="ri-delete-bin-line text-[11px]"></i>
                                    </button>
                                    <div
                                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all ${
                                        isSelected
                                          ? 'border-green-500 bg-green-500'
                                          : 'border-slate-300 group-hover:border-slate-400'
                                      }`}
                                    >
                                      {isSelected && (
                                        <i className="ri-check-line text-white text-[9px]"></i>
                                      )}
                                    </div>
                                  </div>
                                </button>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}

                  {modoChave === 'digitar' && (
                    <div className="space-y-2" data-tour="pix-tipo-chave">
                      <div className="grid grid-cols-2 gap-1.5">
                        {[
                          { id: 'cpf', label: 'CPF/CNPJ', icon: 'ri-user-line' },
                          { id: 'email', label: 'E‑mail', icon: 'ri-mail-line' },
                          { id: 'celular', label: 'Celular', icon: 'ri-smartphone-line' },
                          { id: 'aleatoria', label: 'Chave Aleatória', icon: 'ri-key-line' },
                        ].map((tipo) => (
                          <button
                            key={tipo.id}
                            type="button"
                            onClick={() => {
                              setTipoChave(tipo.id as any);
                              setChave('');
                            }}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 transition-all cursor-pointer ${
                              tipoChave === tipo.id
                                ? 'border-green-500 bg-green-50 text-green-700'
                                : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                            }`}
                          >
                            <i className={`${tipo.icon} text-base`}></i>
                            <span className="text-xs font-medium">{tipo.label}</span>
                          </button>
                        ))}
                      </div>

                      <div>
                        <label htmlFor="chave-pix" className="block text-xs font-medium text-slate-700 mb-1">
                          Chave PIX
                        </label>
                        <input
                          id="chave-pix"
                          data-tour="pix-chave-input"
                          type="text"
                          value={chave}
                          onChange={(e) => setChave(e.target.value)}
                          placeholder={
                            tipoChave === 'cpf'
                              ? '000.000.000-00'
                              : tipoChave === 'email'
                              ? 'email@exemplo.com'
                              : tipoChave === 'celular'
                              ? '(00) 00000-0000'
                              : 'Chave aleatória'
                          }
                          required
                          className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="valor-pix" className="block text-xs font-medium text-slate-700 mb-1.5">
                  Valor do Resgate
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600 font-medium text-sm">
                    R$
                  </span>
                  <input
                    id="valor-pix"
                    type="text"
                    value={
                      valorNumerico > 0
                        ? valorNumerico.toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : '0,00'
                    }
                    readOnly
                    disabled
                    className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-lg bg-slate-100 text-base font-semibold text-slate-700 cursor-not-allowed"
                  />
                </div>
                {valorNumerico > 0 && (
                  <p className="mt-1 text-[11px] text-green-700 font-medium flex items-center gap-1">
                    <i className="ri-coin-line"></i>
                    {pontosNecessarios.toLocaleString('pt-BR')} pontos serão utilizados neste resgate
                  </p>
                )}
                {!valorNumerico && (
                  <p className="mt-1 text-[10px] text-slate-500">
                    <i className="ri-information-line mr-1"></i>
                    O valor é definido pelo crédito selecionado
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="descricao-pix" className="block text-xs font-medium text-slate-700 mb-1.5">
                  Descrição (Opcional)
                </label>
                <input
                  id="descricao-pix"
                  data-tour="pix-descricao"
                  type="text"
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  placeholder="Ex: Pagamento, Transferência..."
                  maxLength={50}
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={animatedClose}
                  className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium cursor-pointer whitespace-nowrap text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-tour="pix-btn-continuar"
                  disabled={!chave || !creditoSelecionado || !valorValido}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-semibold transition-all whitespace-nowrap text-sm ${
                    chave && creditoSelecionado && valorValido
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:shadow-lg cursor-pointer'
                      : 'bg-slate-200 text-slate-500 cursor-not-allowed'
                  }`}
                >
                  Continuar
                </button>
              </div>
            </form>
          )}

          {/* Etapa: Token de Confirmação */}
          {etapa === 'token' && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-xl px-4 py-3 space-y-2">
                <h4 className="text-base font-bold text-slate-900">Resumo da Transferência</h4>
                {creditoAtual && (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-500">Crédito</span>
                      <span className="text-xs font-semibold text-slate-800">{creditoAtual.descricao}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-500">Campanha</span>
                      <span className="text-xs font-medium text-slate-700">{creditoAtual.campanha}</span>
                    </div>
                  </>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">Tipo de Chave</span>
                  <span className="text-xs font-medium text-slate-700">{tipoChaveLabel}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">Chave PIX</span>
                  <span className="text-xs font-semibold text-slate-800">{chave}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-500">Valor a receber</span>
                  <span className="text-base font-bold text-green-600">
                    R${' '}
                    {valorNumerico.toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-1.5 border-t border-slate-200/70">
                  <span className="text-xs font-bold text-slate-700">Pontos utilizados</span>
                  <span className="text-base font-bold text-orange-600">
                    {pontosNecessarios.toLocaleString('pt-BR')} pts
                  </span>
                </div>
                {descricao && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-500">Descrição</span>
                    <span className="text-xs font-medium text-slate-700">{descricao}</span>
                  </div>
                )}
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-green-700">Pontos após transferência</span>
                  <span className="text-base font-bold text-green-600">
                    {(saldoDisponivel - calcularPontosFinanceiro(valorNumerico)).toLocaleString('pt-BR')} pts
                  </span>
                </div>
              </div>

              <div className="text-center space-y-3">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                  <i className="ri-shield-keyhole-line text-xl text-amber-600"></i>
                </div>
                <div>
                  <h4 className="text-base font-bold text-slate-900">Token de Segurança</h4>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Enviamos um código de 6 dígitos para seu e‑mail e celular cadastrados
                  </p>
                </div>

                <div className="flex justify-center gap-2" onPaste={handleTokenPaste}>
                  {tokenDigits.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => {
                        inputRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleTokenChange(index, e.target.value)}
                      onKeyDown={(e) => handleTokenKeyDown(index, e)}
                      className={`w-11 h-12 text-center text-lg font-bold border-2 rounded-lg focus:outline-none transition-all ${
                        tokenErro
                          ? 'border-red-400 bg-red-50 text-red-600 focus:ring-2 focus:ring-red-300'
                          : digit
                          ? 'border-green-400 bg-green-50 text-green-700 focus:ring-2 focus:ring-green-300'
                          : 'border-slate-300 bg-white text-slate-800 focus:ring-2 focus:ring-green-400 focus:border-green-400'
                      }`}
                    />
                  ))}
                </div>

                {tokenErro && (
                  <div className="flex items-center justify-center gap-2 text-red-600 bg-red-50 rounded-lg py-2 px-4">
                    <i className="ri-error-warning-line text-sm"></i>
                    <span className="text-xs font-medium">{tokenErro}</span>
                  </div>
                )}

                <div className="text-xs text-slate-500">
                  Não recebeu o código?{' '}
                  {reenvioTimer > 0 ? (
                    <span className="text-slate-400">
                      Reenviar em{' '}
                      <span className="font-semibold text-slate-600">{reenvioTimer}s</span>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleReenviarToken}
                      className="text-green-600 font-semibold hover:text-green-700 hover:underline cursor-pointer"
                    >
                      Reenviar código
                    </button>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setEtapa('formulario');
                    setTokenDigits(['', '', '', '', '', '']);
                    setTokenErro('');
                  }}
                  disabled={processando}
                  className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium disabled:opacity-50 cursor-pointer whitespace-nowrap text-sm"
                >
                  Voltar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmarToken}
                  disabled={!tokenCompleto || processando}
                  data-tour="pix-btn-confirmar"
                  className={`flex-1 px-4 py-2.5 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 whitespace-nowrap text-sm ${
                    !tokenCompleto || processando
                      ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:shadow-lg cursor-pointer'
                  }`}
                >
                  {processando ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Validando...
                    </>
                  ) : (
                    <>
                      <i className="ri-shield-check-line"></i>
                      Confirmar PIX
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Etapa: Sucesso */}
          {etapa === 'sucesso' && (
            <div className="text-center space-y-3">
              <div className="relative">
                <div
                  className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto animate-bounce"
                  style={{ animationDuration: '1s', animationIterationCount: '2' }}
                >
                  <i className="ri-check-double-line text-3xl text-green-600"></i>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="w-20 h-20 rounded-full border-2 border-green-300 animate-ping opacity-30"
                    style={{ animationDuration: '1.5s', animationIterationCount: '2' }}
                  ></div>
                </div>
              </div>

              <div>
                <h4 className="text-xl font-bold text-slate-900 mb-0.5">PIX Realizado!</h4>
                <p className="text-xs text-slate-500">Sua transferência foi processada com sucesso</p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="bg-green-50 border border-green-200 rounded-xl p-2.5">
                  <div className="text-[10px] text-green-600 font-medium uppercase tracking-wider mb-1">
                    ID da Transação
                  </div>
                  <div className="flex items-center justify-center gap-1">
                    <span className="text-xs font-bold text-green-800 font-mono tracking-wide truncate">
                      {transacaoId.current}
                    </span>
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(transacaoId.current);
                          setCopiado(true);
                          setTimeout(() => setCopiado(false), 1500);
                        } catch {
                          /* ignore */
                        }
                      }}
                      className="w-5 h-5 flex items-center justify-center rounded hover:bg-green-100 transition-colors cursor-pointer flex-shrink-0"
                      title="Copiar ID"
                    >
                      <i
                        className={`${
                          copiado ? 'ri-check-line text-green-600' : 'ri-file-copy-line text-green-500'
                        } text-xs`}
                      ></i>
                    </button>
                  </div>
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-2.5">
                  <div className="text-[10px] text-emerald-600 font-medium uppercase tracking-wider mb-1">
                    Cód. Validação
                  </div>
                  <div className="flex items-center justify-center gap-1">
                    <i className="ri-shield-check-line text-emerald-500 text-xs flex-shrink-0"></i>
                    <span className="text-xs font-bold text-emerald-800 font-mono tracking-wider truncate">
                      {codigoValidacao.current}
                    </span>
                    <button
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(codigoValidacao.current);
                          setCopiado(true);
                          setTimeout(() => setCopiado(false), 1500);
                        } catch {
                          /* ignore */
                        }
                      }}
                      className="w-5 h-5 flex items-center justify-center rounded hover:bg-emerald-100 transition-colors cursor-pointer flex-shrink-0"
                      title="Copiar código"
                    >
                      <i
                        className={`${
                          copiado ? 'ri-check-line text-emerald-600' : 'ri-file-copy-line text-emerald-500'
                        } text-xs`}
                      ></i>
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl px-4 py-2 text-left border border-slate-100">
                <div className="flex justify-between items-center py-1.5 border-b border-slate-200/70">
                  <span className="text-[10px] text-slate-500">Crédito Resgatado</span>
                  <span className="text-xs font-semibold text-slate-800">{creditoAtual?.descricao}</span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b border-slate-200/70">
                  <span className="text-[10px] text-slate-500">Campanha</span>
                  <span className="text-xs font-medium text-slate-700">{creditoAtual?.campanha}</span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b border-slate-200/70">
                  <span className="text-[10px] text-slate-500">Tipo de Chave</span>
                  <span className="text-xs font-medium text-slate-700">{tipoChaveLabel}</span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b border-slate-200/70">
                  <span className="text-[10px] text-slate-500">Chave PIX</span>
                  <span className="text-xs font-semibold text-slate-800">{chave}</span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b border-slate-200/70">
                  <span className="text-[10px] text-slate-500">Valor Recebido</span>
                  <span className="text-base font-bold text-green-600">
                    R${' '}
                    {valorNumerico.toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b border-slate-200/70">
                  <span className="text-[10px] text-slate-500">Pontos utilizados</span>
                  <span className="text-sm font-bold text-orange-600">
                    {pontosNecessarios.toLocaleString('pt-BR')} pts
                  </span>
                </div>
                {descricao && (
                  <div className="flex justify-between items-center py-1.5 border-b border-slate-200/70">
                    <span className="text-[10px] text-slate-500">Descrição</span>
                    <span className="text-xs font-medium text-slate-700">{descricao}</span>
                  </div>
                )}
                <div className="flex justify-between items-center py-1">
                  <span className="text-[10px] text-slate-500">Data e Hora</span>
                  <span className="text-xs font-semibold text-slate-800">
                    {dataHoraTransacao.current || new Date().toLocaleString('pt-BR')}
                  </span>
                </div>
              </div>

              <div className="flex gap-2 justify-center">
                <button
                  onClick={gerarComprovantePDF}
                  disabled={salvandoPdf}
                  className="flex flex-col items-center justify-center gap-1 px-3 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-colors font-medium cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {salvandoPdf ? (
                    <i className="ri-loader-4-line animate-spin text-lg"></i>
                  ) : (
                    <i className="ri-file-pdf-2-line text-lg text-red-500"></i>
                  )}
                  <span className="text-[10px]">Salvar PDF</span>
                </button>
                <button
                  onClick={handleSalvarImagem}
                  disabled={salvandoImagem}
                  className="flex flex-col items-center justify-center gap-1 px-3 py-2.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-xl transition-colors font-medium cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {salvandoImagem ? (
                    <i className="ri-loader-4-line animate-spin text-lg"></i>
                  ) : (
                    <i className="ri-image-line text-lg"></i>
                  )}
                  <span className="text-[10px]">Salvar Imagem</span>
                </button>
                <button
                  onClick={() => {
                    setEmailModalOpen(true);
                    setEmailEnviado(false);
                    setEmailErro('');
                  }}
                  className="flex flex-col items-center justify-center gap-1 px-3 py-2.5 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-xl transition-colors font-medium cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-send-line text-lg"></i>
                  <span className="text-[10px]">Enviar E‑mail</span>
                </button>
                <button
                  onClick={handleCompartilhar}
                  disabled={compartilhando}
                  className="flex flex-col items-center justify-center gap-1 px-3 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl transition-colors font-medium cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {compartilhando ? (
                    <i className="ri-loader-4-line animate-spin text-lg"></i>
                  ) : (
                    <i className="ri-share-line text-lg"></i>
                  )}
                  <span className="text-[10px]">Compartilhar</span>
                </button>
              </div>

              <button
                onClick={animatedClose}
                className="w-full px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:shadow-lg transition-all font-semibold cursor-pointer whitespace-nowrap text-sm"
              >
                Fechar
              </button>
            </div>
          )}

          {/* Modal de Envio por E‑mail */}
          {emailModalOpen && (
            <div
              className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4"
              onClick={() => {
                if (!enviandoEmail) {
                  setEmailModalOpen(false);
                  setEmailErro('');
                }
              }}
            >
              <div
                className="bg-white rounded-2xl max-w-sm w-full shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-5 text-white relative">
                  <button
                    onClick={() => {
                      if (!enviandoEmail) {
                        setEmailModalOpen(false);
                        setEmailErro('');
                      }
                    }}
                    className="absolute top-3 right-3 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-lg"></i>
                  </button>
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 bg-white/20 rounded-full flex items-center justify-center">
                      <i className="ri-mail-send-line text-2xl"></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold">Enviar Comprovante</h4>
                      <p className="text-xs opacity-90">Receba o comprovante no e‑mail</p>
                    </div>
                  </div>
                </div>

                <div className="p-5 space-y-4">
                  {emailEnviado ? (
                    <div className="text-center py-4 space-y-3">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                        <i className="ri-mail-check-line text-3xl text-green-600"></i>
                      </div>
                      <div>
                        <h5 className="text-lg font-bold text-slate-900">E‑mail Enviado!</h5>
                        <p className="text-sm text-slate-500 mt-1">
                          O comprovante foi enviado para
                        </p>
                        <p className="text-sm font-semibold text-slate-800 mt-0.5">{emailDestinatario}</p>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-left">
                        <div className="flex items-start gap-2">
                          <i className="ri-information-line text-green-600 text-sm mt-0.5"></i>
                          <p className="text-xs text-green-700">
                            Verifique a caixa de entrada e a pasta de spam. O e‑mail pode levar alguns
                            minutos para chegar.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2">
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-file-text-line text-slate-400"></i>
                          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            Preview do Comprovante
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-slate-500">Transação</span>
                          <span className="text-xs font-mono font-semibold text-slate-700">
                            {transacaoId.current}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-slate-500">Valor</span>
                          <span className="text-sm font-bold text-green-600">
                            R${' '}
                            {valorNumerico.toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-slate-500">Chave PIX</span>
                          <span className="text-xs font-medium text-slate-700">{chave}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-slate-500">Data</span>
                          <span className="text-xs font-medium text-slate-700">
                            {dataHoraTransacao.current}
                          </span>
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="email-comprovante"
                          className="block text-sm font-medium text-slate-700 mb-1.5"
                        >
                          E‑mail do destinatário
                        </label>
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                            <i className="ri-mail-line text-slate-400"></i>
                          </div>
                          <input
                            id="email-comprovante"
                            type="email"
                            value={emailDestinatario}
                            onChange={(e) => {
                              setEmailDestinatario(e.target.value);
                              setEmailErro('');
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !enviandoEmail) {
                                handleEnviarEmail();
                              }
                            }}
                            placeholder="exemplo@email.com"
                            disabled={enviandoEmail}
                            className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 text-sm disabled:opacity-50 disabled:bg-slate-50"
                            autoFocus
                          />
                        </div>
                        {emailErro && (
                          <div className="flex items-center gap-1.5 mt-1.5">
                            <i className="ri-error-warning-line text-red-500 text-sm"></i>
                            <span className="text-xs text-red-600">{emailErro}</span>
                          </div>
                        )}
                      </div>

                      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <i className="ri-information-line text-amber-600 text-sm mt-0.5"></i>
                        <p className="text-xs text-amber-700">
                          Será enviado o comprovante completo com todos os dados da transferência,
                          incluindo logo da empresa, código de autenticidade e QR Code de validação.
                        </p>
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={() => {
                            setEmailModalOpen(false);
                            setEmailErro('');
                          }}
                          disabled={enviandoEmail}
                          className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium cursor-pointer whitespace-nowrap disabled:opacity-50"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={handleEnviarEmail}
                          disabled={enviandoEmail || !emailDestinatario}
                          className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 whitespace-nowrap ${
                            enviandoEmail || !emailDestinatario
                              ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                              : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-lg cursor-pointer'
                          }`}
                        >
                          {enviandoEmail ? (
                            <>
                              <i className="ri-loader-4-line animate-spin"></i>
                              Enviando...
                            </>
                          ) : (
                            <>
                              <i className="ri-send-plane-fill"></i>
                              Enviar
                            </>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Toast: conta definida como principal */}
          {toastPrincipal && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] animate-fade-in-up">
              <div className="flex items-center gap-3 bg-white border border-amber-200 shadow-xl rounded-2xl px-5 py-3.5 min-w-[280px]">
                <div className="w-9 h-9 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-star-fill text-amber-500 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800">Conta principal atualizada!</p>
                  <p className="text-xs text-slate-500 mt-0.5">{toastPrincipal} agora é sua conta principal</p>
                </div>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-green-500 text-base"></i>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
